import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, MenuItem, Button,
  FormControl, InputLabel, Select, Card, CardContent,
  Avatar, alpha, CircularProgress, InputAdornment, Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  Flag as FlagIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, startOfToday, startOfDay, isBefore, differenceInCalendarDays } from 'date-fns';
import { motion } from 'framer-motion';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import SearchableSelect from '../../common/SearchableSelect';
import InfoField from '../../common/InfoField';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import CommonSectionCard from '../../common/SectionCard';
import { canModifyPenalty } from '../../../utils/eventPenaltyPermissions';
import { formatFieldErrorsMessage, rejectConfirmValidation } from '../../../utils/formValidationFeedback';
import { makePickerSlots, buildPickerFieldProps } from '../../common/formFieldSx';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = {
  600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A',
};
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#94A3B8';
const DUE_CORAL  = '#F87171';
const DUE_AMBER  = '#FBBF24';

const getPenaltyDueDateNeon = (penalty) => {
  if (!penalty?.due_date) return NEON;
  const dueDate = new Date(penalty.due_date);
  const daysLeft = differenceInCalendarDays(startOfDay(dueDate), startOfDay(new Date()));
  const isDone = penalty.status === 'done';
  const isOverdue = !isDone && daysLeft < 0;
  const isDueSoon = !isDone && daysLeft >= 0 && daysLeft <= 3;
  if (isOverdue) return DUE_CORAL;
  if (isDueSoon) return DUE_AMBER;
  return NEON3;
};

const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;
const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

// ─── Global CSS ────────────────────────────────────────────────────────────────
const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }

  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.22) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.65) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.65rem !important;
    font-weight: 600 !important;
    letter-spacing: 1px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.18) !important;
    color: #38BDF8 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.3) !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    border: 2px solid #38BDF8 !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.6) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid rgba(56,189,248,0.65) !important;
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.08) !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.75) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiYearCalendar-root button,
  .MuiMonthCalendar-root button {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiYearCalendar-root button:hover,
  .MuiMonthCalendar-root button:hover {
    background: rgba(56,189,248,0.1) !important;
    color: #38BDF8 !important;
  }
  .MuiYearCalendar-root button.Mui-selected,
  .MuiMonthCalendar-root button.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.5) !important;
    border-radius: 8px !important;
  }
  .MuiPickersToolbar-root {
    background: #0f1f38 !important;
    border-bottom: 1px solid rgba(56,189,248,0.12) !important;
  }
  .MuiPickersToolbar-title,
  .MuiDateTimePickerToolbar-separator { color: #38BDF8 !important; }
  .MuiDialogActions-root .MuiButton-root {
    color: #38BDF8 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 600 !important;
  }
  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root,
  .MuiPickersOutlinedInput-root {
    border-radius: 10px !important;
    background: rgba(15, 31, 56, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    transition: box-shadow 0.2s ease, border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.18) !important;
    transition: border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersTextField-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.4) !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.65) !important;
    border-width: 1.5px !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused,
  .MuiPickersOutlinedInput-root.Mui-focused {
    box-shadow: 0 0 0 3px rgba(56,189,248,0.12), 0 0 14px rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.875rem !important;
    color: rgba(56,189,248,0.45) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root.Mui-focused {
    color: rgba(56,189,248,0.85) !important;
    font-weight: 600 !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionStart .MuiSvgIcon-root {
    color: rgba(56,189,248,0.45) !important;
    font-size: 18px !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root {
    color: rgba(56,189,248,0.35) !important;
    padding: 6px !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root:hover {
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiSvgIcon-root {
    font-size: 17px !important;
  }
  .MuiPickersSectionList-root {
    color: rgba(226,232,240,0.85) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.9rem !important;
  }
  .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-separator {
    color: rgba(226,232,240,0.85) !important;
    -webkit-text-fill-color: rgba(226,232,240,0.85) !important;
  }
  .MuiPickersSectionList-sectionContent[data-value=""] {
    color: rgba(56,189,248,0.3) !important;
    -webkit-text-fill-color: rgba(56,189,248,0.3) !important;
  }
  .MuiPickersSectionList-section:focus .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-sectionContent:focus {
    background: rgba(56,189,248,0.15) !important;
    border-radius: 3px !important;
    color: #38BDF8 !important;
    -webkit-text-fill-color: #38BDF8 !important;
    outline: none !important;
  }
`;

const MotionCard = motion(Card);

// ─── Card shell ────────────────────────────────────────────────────────────────
const cardShellSx = {
  borderRadius: '14px',
  background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.97)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
  border: `1px solid ${alpha(NEON, 0.17)}`,
  boxShadow: [
    `0 0 0 1px ${alpha(B[900], 0.9)}`,
    `0 4px 24px ${alpha('#000', 0.45)}`,
    `0 20px 60px ${alpha('#000', 0.3)}`,
    `inset 0 1px 0 ${alpha(NEON, 0.1)}`,
  ].join(', '),
  backdropFilter: 'blur(20px)',
  overflow: 'hidden',
  position: 'relative',
  transition: 'box-shadow 0.28s ease, transform 0.28s ease',
  '&:hover': {
    boxShadow: [
      `0 0 0 1px ${alpha(B[900], 0.9)}`,
      `0 8px 36px ${alpha('#000', 0.5)}`,
      `0 24px 72px ${alpha('#000', 0.35)}`,
      `inset 0 1px 0 ${alpha(NEON, 0.15)}`,
      `0 0 0 1px ${alpha(NEON, 0.1)}`,
    ].join(', '),
    transform: 'translateY(-2px)',
  },
  '&::before': {
    content: '""', display: 'block', height: '2px',
    background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.8)} 30%, ${alpha(NEON2, 0.9)} 50%, ${alpha(NEON, 0.8)} 70%, transparent)`,
  },
};

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── InfoField wrapper — matches EventDetails pattern ─────────────────────────
// Wraps common InfoField in the same flex tile layout used by EventDetails
const DetailInfoField = ({ icon: Icon, label, value, fullWidth = false }) => (
  <Box sx={{ flex: fullWidth ? '1 1 100%' : '1 1 240px', minWidth: fullWidth ? '100%' : '200px' }}>
    <InfoField
      icon={Icon ? <Icon sx={{ fontSize: 14 }} /> : null}
      label={label}
      value={value || '—'}
    />
  </Box>
);

// ─── Status badge tile — mirrors PeriodCard neon-tinted style ─────────────────
const StatusBadgeTile = ({ status, neon }) => (
  <Box
    sx={{
      flex: '1 1 calc(50% - 8px)',
      minWidth: '200px',
      p: 2.5,
      borderRadius: '12px',
      background: alpha(neon, 0.07),
      border: `1px solid ${alpha(neon, 0.22)}`,
      transition: 'all 0.22s ease',
      '&:hover': { background: alpha(neon, 0.12), transform: 'translateY(-2px)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <FlagIcon sx={{ fontSize: 14, color: neon }} />
      <Typography
        sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.62rem',
          fontWeight: 600,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: alpha(neon, 0.75),
        }}
      >
        Status
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: neon,
          boxShadow: `0 0 8px ${neon}`,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 600,
          fontSize: '0.9rem',
          color: TEXT_BODY,
        }}
      >
        {status}
      </Typography>
    </Box>
  </Box>
);

// ─── Due Date tile — mirrors PeriodCard style ─────────────────────────────────
const DueDateTile = ({ dueDate, neon = NEON }) => (
  <Box
    sx={{
      flex: '1 1 calc(50% - 8px)',
      minWidth: '200px',
      p: 2.5,
      borderRadius: '12px',
      background: alpha(neon, 0.07),
      border: `1px solid ${alpha(neon, 0.22)}`,
      transition: 'all 0.22s ease',
      '&:hover': { background: alpha(neon, 0.12), transform: 'translateY(-2px)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <CalendarTodayIcon sx={{ fontSize: 14, color: neon }} />
      <Typography
        sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.62rem',
          fontWeight: 600,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: alpha(neon, 0.75),
        }}
      >
        Due Date
      </Typography>
    </Box>
    <Typography
      sx={{
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: '1.05rem',
        color: TEXT_BODY,
      }}
    >
      {dueDate}
    </Typography>
  </Box>
);

// ─── Shared inputRootSx ────────────────────────────────────────────────────────
const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85),
    background: alpha(SURFACE_UP, 0.7),
    backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.18), transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': {
    color: `${alpha(TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important`,
    '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45),
  },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
  '& .MuiOutlinedInput-root .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
};

// ─── StyledField ──────────────────────────────────────────────────────────────
const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

// ─── StyledSelect ─────────────────────────────────────────────────────────────
const StyledSelect = ({ label, value, onChange, name, children, required, startIcon, error, helperText, sx }) => (
  <FormControl fullWidth required={required} error={error} sx={{ flex: 1, ...inputRootSx, ...sx }}>
    <InputLabel sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45), '&.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 } }}>
      {label}
    </InputLabel>
    <Select
      name={name}
      value={value}
      onChange={onChange}
      label={label}
      startAdornment={startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined}
      MenuProps={{
        PaperProps: {
          sx: {
            background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
            border: `1px solid ${alpha(NEON, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
            backdropFilter: 'blur(12px)',
            '& .MuiMenuItem-root': {
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: alpha(TEXT_BODY, 0.85),
              transition: 'all 0.15s ease',
              '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
              '&.Mui-selected': {
                background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`, color: NEON, fontWeight: 600,
                '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` },
              },
            },
          },
        },
      }}
      sx={{
        borderRadius: '10px',
        fontFamily: "'DM Sans', sans-serif",
        color: alpha(TEXT_BODY, 0.85),
        background: alpha(SURFACE_UP, 0.7),
        backdropFilter: 'blur(8px)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.18) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}` },
        '& .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
        '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      }}
    >
      {children}
    </Select>
    {helperText && (
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: '#F87171', mt: 0.5, ml: 1.5 }}>
        {helperText}
      </Typography>
    )}
  </FormControl>
);

// ─── Status options ────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', icon: <CancelIcon />,         neon: '#F87171' },
  { value: 'in_progress', label: 'In Progress', icon: <HourglassEmptyIcon />, neon: '#FCD34D' },
  { value: 'done',        label: 'Done',        icon: <CheckIcon />,          neon: NEON3     },
];

// ─── Helper: get status neon color ────────────────────────────────────────────
const getStatusNeon = (statusValue) => {
  const opt = STATUS_OPTIONS.find(o => o.value === statusValue);
  return opt ? opt.neon : alpha(NEON, 0.5);
};

// ─── Main Component ────────────────────────────────────────────────────────────
const PenaltyForm = ({ open, onClose, penalty: propPenalty, eventId: propEventId, isPage = false, isDetails = false }) => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const location  = useLocation();
  const { user }  = useAuth();

  const [loading,                 setLoading]                 = useState(false);
  const [dataLoading,             setDataLoading]             = useState(() => Boolean(isPage || open));
  const [error,                   setError]                   = useState(null);
  const [successMessage,          setSuccessMessage]          = useState(null);
  const [users,                   setUsers]                   = useState([]);
  const [events,                  setEvents]                  = useState([]);
  const [penalty,                 setPenalty]                 = useState(propPenalty || location.state?.penalty);
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest,    setPendingUpdateRequest]    = useState(null);
  const [deleteDialogOpen,        setDeleteDialogOpen]        = useState(false);
  const [pickerOpen,              setPickerOpen]              = useState({});

  const [formData, setFormData] = useState({
    title: '', description: '', assigned_to_id: '',
    status: 'not_started', due_date: new Date(),
    notes: '', event_id: propEventId || '',
  });

  const isReadOnly = Boolean(isDetails && isPage);
  const isUpdatingExisting = Boolean(!isReadOnly && (id || penalty));

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'penalty',
    userId: user?.id,
    isEditMode: isUpdatingExisting || isReadOnly,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  // ── Inject CSS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const cssId = 'penaltyform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        const requests = [axiosInstance.get('/users?minimal=1&active_only=1')];
        if (isPage) requests.push(axiosInstance.get('/events?minimal=1'));
        if (id && isPage) requests.push(axiosInstance.get(`/penalties/${id}`));

        const [usersRes, eventsRes, penaltyRes] = await Promise.all(requests);
        setUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
        if (eventsRes) setEvents(Array.isArray(eventsRes?.data) ? eventsRes.data : []);
        if (penaltyRes?.data) {
          setPenalty(penaltyRes.data);
          setFormData({ ...penaltyRes.data, due_date: new Date(penaltyRes.data.due_date) });
        }
        setError(null);
      } catch {
        setError('Failed to fetch data');
      } finally {
        setDataLoading(false);
      }
    };
    if (isPage || open) fetchData();
  }, [open, isPage, id]);

  useEffect(() => {
    if (penalty) setFormData({ ...penalty, due_date: new Date(penalty.due_date) });
    else if (!id) setFormData({ title: '', description: '', assigned_to_id: '', status: 'not_started', due_date: new Date(), notes: '', event_id: propEventId || '' });
  }, [penalty, id, propEventId]);

  const collectValidationErrors = (data = formData) => {
    const errors = {};
    if (!data.title?.trim()) errors.title = 'Title is required';
    if (!data.description?.trim()) errors.description = 'Description is required';
    if (!data.assigned_to_id) errors.assigned_to_id = 'Assigned user is required';
    if (!data.due_date) errors.due_date = 'Due date is required';
    else if (isBefore(startOfDay(data.due_date), startOfToday())) {
      errors.due_date = 'Due date cannot be before today';
    }
    if (isPage && !isReadOnly && !data.event_id) errors.event_id = 'Event is required';
    else if (isPage && !isUpdatingExisting && data.event_id) {
      const selectedEvent = events.find((event) => String(event.id) === String(data.event_id));
      if (String(selectedEvent?.status || '').toLowerCase() === 'cancelled') {
        errors.event_id = 'Cancelled events cannot be selected for new penalties';
      }
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAssignedToChange = (userId) => {
    setFormData((prev) => ({ ...prev, assigned_to_id: userId }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors.assigned_to_id) {
      setValidationErrors((prev) => ({ ...prev, assigned_to_id: '' }));
    }
  };

  const handleEventChange = (eventId) => {
    setFormData((prev) => ({ ...prev, event_id: eventId }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors.event_id) {
      setValidationErrors((prev) => ({ ...prev, event_id: '' }));
    }
  };

  const handleDueDateChange = (date) => {
    setFormData((prev) => ({ ...prev, due_date: date }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors.due_date) {
      setValidationErrors((prev) => ({ ...prev, due_date: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    if (isUpdatingExisting) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setPendingUpdateRequest(formData);
      setConfirmUpdateDialogOpen(true);
      return;
    }
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowFieldErrors(true);
      setError(formatFieldErrorsMessage(errors));
      return;
    }
    setShowFieldErrors(false);

    try {
      setLoading(true); setError(null);
      await axiosInstance.post('/penalties', formData);
      if (!isUpdatingExisting) formDraft.clearDraft();
      setSuccessMessage('Penalty created successfully!');
      setTimeout(() => { if (isPage) navigate('/penalties'); else onClose(); }, 2000);
    } catch (err) {
      setError(err.response?.data?.errors
        ? Object.entries(err.response.data.errors).map(([f, m]) => `${f}: ${m.join(', ')}`).join('\n')
        : err.response?.data?.message || 'Failed to save penalty');
    } finally { setLoading(false); }
  };

  const confirmUpdate = async () => {
    if (!pendingUpdateRequest) return;
    const errors = collectValidationErrors(pendingUpdateRequest);
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setPendingUpdateRequest,
        setShowFieldErrors,
      });
      return;
    }
    try {
      setLoading(true); setError(null);
      const response = await axiosInstance.put(`/penalties/${penalty?.id || id}`, pendingUpdateRequest);
      if (response?.data) {
        const updatedPenalty = response.data;
        setPenalty(updatedPenalty);
        setFormData({ ...updatedPenalty, due_date: new Date(updatedPenalty.due_date) });
      }
      formDraft.clearDraft();
      setSuccessMessage('Penalty updated successfully!');
      setTimeout(() => { if (isPage) navigate('/penalties'); else onClose(); }, 2000);
    } catch (err) {
      setError(err.response?.data?.errors
        ? Object.entries(err.response.data.errors).map(([f, m]) => `${f}: ${m.join(', ')}`).join('\n')
        : err.response?.data?.message || 'Failed to update penalty');
    } finally { setLoading(false); setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }
  };

  const isFormValid = Boolean(
    formData.title?.trim() &&
    formData.description?.trim() &&
    formData.assigned_to_id &&
    formData.due_date &&
    (!isPage || isReadOnly || formData.event_id)
  );
  const dueDatePickerSlots = makePickerSlots('normal', <CalendarTodayIcon sx={{ color: alpha(NEON, 0.45) }} />);
  const fieldHelper = (name) => (
    showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined
  );
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];
  const dueDatePickerProps = buildPickerFieldProps('due_date', pickerOpen, setPickerOpen, dueDatePickerSlots(isReadOnly), {
    disabled: isReadOnly,
    textField: {
      fullWidth: true,
      required: true,
      error: fieldHasError('due_date'),
      helperText: fieldHelper('due_date'),
    },
  });

  const isCancelledEvent = (event) => String(event?.status || '').toLowerCase() === 'cancelled';
  const selectableEvents = events.filter((event) => {
    const isCurrentEvent = (isUpdatingExisting || isReadOnly)
      && String(event.id) === String(formData.event_id || penalty?.event_id || '');
    return !isCancelledEvent(event) || isCurrentEvent;
  });

  const getUserName  = (uid) => users.find((u) => String(u.id) === String(uid))?.name || '—';
  const getEventName = (eid) => events.find((e) => String(e.id) === String(eid))?.name || '—';
  const canEdit      = canModifyPenalty(user, penalty);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/penalties/${id}`);
      setDeleteDialogOpen(false);
      setSuccessMessage('Penalty deleted successfully.');
      setTimeout(() => navigate('/penalties'), 2000);
    } catch {
      setError('Failed to delete penalty.');
      setDeleteDialogOpen(false);
    }
  };

  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message={isReadOnly ? 'Loading penalty details' : 'Loading penalty form'} size={56} variant="primary" />
    </Box>
  );

  if (isReadOnly && id && !penalty) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Header
          title="Penalty Details"
          subtitle="Official penalty record"
          icon={AssignmentIcon}
          showBackButton
          onBackClick={() => navigate('/penalties')}
        />
        <FeedbackMessage
          message={error || 'Penalty not found'}
          severity="error"
          onClose={() => setError(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
        />
      </Container>
    );
  }

  // ─── Details read-only view — matches EventDetails layout ──────────────────
  const renderDetailsView = () => {
    const statusOption = STATUS_OPTIONS.find(o => o.value === penalty.status);
    const statusLabel  = statusOption?.label || penalty.status || '—';
    const statusNeon   = getStatusNeon(penalty.status);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── Penalty Information — mirrors EventDetails "Event Information" ── */}
        <SectionCard title="Penalty Information" icon={<AssignmentIcon />} delay={0}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {penalty.event_id && (
              <DetailInfoField icon={EventIcon} label="Event" value={getEventName(penalty.event_id)} />
            )}
            <DetailInfoField icon={TitleIcon}   label="Penalty Title" value={penalty.title} />
            <DetailInfoField icon={PersonIcon}  label="Assigned To"   value={getUserName(penalty.assigned_to_id)} />
            {penalty.description && (
              <DetailInfoField icon={DescriptionIcon} label="Description" value={penalty.description} fullWidth />
            )}
          </Box>
        </SectionCard>

        {/* ── Assignment & Status — neon tiles matching EventDetails PeriodCard pattern ── */}
        <SectionCard title="Assignment & Status" icon={<ScheduleIcon />} delay={0.08} accentNeon={NEON2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <DueDateTile
              dueDate={penalty.due_date ? format(new Date(penalty.due_date), 'MMMM d, yyyy') : '—'}
              neon={getPenaltyDueDateNeon(penalty)}
            />
            <StatusBadgeTile
              status={statusLabel}
              neon={statusNeon}
            />
          </Box>
        </SectionCard>

        {/* ── Additional Notes (only shown if present) ── */}
        {penalty.notes && (
          <SectionCard title="Additional Notes" icon={<NotesIcon />} delay={0.14} accentNeon={NEON3}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <DetailInfoField icon={NotesIcon} label="Notes" value={penalty.notes} fullWidth />
            </Box>
          </SectionCard>
        )}

        {/* ── System Information — mirrors EventDetails "System Information" ── */}
        <SectionCard title="System Information" icon={<ScheduleIcon />} delay={0.2} accentNeon={alpha(NEON, 0.55)}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <DetailInfoField
              icon={ScheduleIcon}
              label="Created"
              value={penalty.created_at ? format(new Date(penalty.created_at), 'MMM d, yyyy · HH:mm') : '—'}
            />
            <DetailInfoField
              icon={PersonIcon}
              label="Created By"
              value={penalty.createdBy?.name || getUserName(penalty.created_by) || '—'}
            />
            {penalty.updated_at && (
              <DetailInfoField
                icon={ScheduleIcon}
                label="Last Updated"
                value={format(new Date(penalty.updated_at), 'MMM d, yyyy · HH:mm')}
              />
            )}
            <DetailInfoField
              icon={PersonIcon}
              label="Updated By"
              value={penalty.updatedBy?.name || getUserName(penalty.updated_by) || '—'}
            />
          </Box>
        </SectionCard>
      </Box>
    );
  };

  const formContent = (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

        <Header
          title={
            isReadOnly ? 'Penalty Details' : isUpdatingExisting ? 'Edit Penalty' : 'Create New Penalty'
          }
          subtitle={
            isReadOnly
              ? 'View penalty information and assignment details'
              : isUpdatingExisting
                ? 'Update penalty information and tracking details'
                : 'Define a new penalty and assign it to a team member'
          }
          icon={AssignmentIcon}
          showBackButton={isPage}
          onBackClick={() => navigate('/penalties')}
          actionButton={
            isReadOnly && canEdit && id ? (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate(`/penalties/edit/${id}`)}
                  sx={{
                    borderRadius: '10px', px: 2.5, py: 0.7,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                    background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`,
                    color: NEON,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(NEON, 0.08),
                      borderColor: NEON,
                      boxShadow: `0 0 12px ${alpha(NEON, 0.2)}`,
                    },
                  }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderRadius: '10px', px: 2.5, py: 0.7,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                    background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.6)}`,
                    color: '#F87171',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha('#F87171', 0.08),
                      borderColor: '#F87171',
                      boxShadow: `0 0 12px ${alpha('#F87171', 0.2)}`,
                    },
                  }}
                >
                  Delete
                </Button>
              </Box>
            ) : null
          }
        />

        <FeedbackMessage
          message={error} severity="error" onClose={() => setError(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
        />
        <FeedbackMessage
          message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: `linear-gradient(135deg,${alpha(NEON3,0.1)},${alpha(SURFACE_MID,0.95)})`, border: `1px solid ${alpha(NEON3,0.35)}`, boxShadow: `0 4px 20px ${alpha(NEON3,0.15)}`, color: alpha(NEON3,0.95) }}
        />

        {formDraft.showNotice && (
          <FormDraftNotice
            savedAt={formDraft.restoredAt}
            onDiscard={formDraft.discardDraft}
            onDismiss={formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          {isReadOnly && penalty ? (
            renderDetailsView()
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <SectionCard title="Penalty Details" icon={<AssignmentIcon />} delay={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                  {isPage && (
                    <>
                      <SearchableSelect
                        required
                        label="Event"
                        value={formData.event_id}
                        onChange={handleEventChange}
                        options={selectableEvents.map((ev) => ({ value: ev.id, label: ev.name }))}
                        startIcon={<EventIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                        placeholder=""
                        sx={{
                          ...(fieldHasError('event_id')
                            ? { '& .MuiFormHelperText-root': { color: '#F87171' } }
                            : {}),
                        }}
                      />
                      {fieldHelper('event_id') && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#F87171', mt: 0.5, ml: 1.75 }}>
                          {fieldHelper('event_id')}
                        </Typography>
                      )}
                    </>
                  )}

                  <StyledField
                    fullWidth required label="Penalty Title" name="title" value={formData.title}
                    onChange={handleChange} error={fieldHasError('title')} helperText={fieldHelper('title')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />

                  <StyledField
                    fullWidth required multiline rows={4} label="Description" name="description" value={formData.description}
                    onChange={handleChange} error={fieldHasError('description')} helperText={fieldHelper('description')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18, alignSelf: 'flex-start', mt: 1.5 }} /></InputAdornment> }}
                  />
                </Box>
              </SectionCard>

              <SectionCard
                title="Assignment Details"
                icon={<PersonIcon />}
                delay={0.08}
                accentNeon={NEON2}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2.5,
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Assigned To */}
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <SearchableSelect
                      required
                      label="Assigned To"
                      value={formData.assigned_to_id}
                      onChange={handleAssignedToChange}
                      options={users.map((u) => ({
                        value: u.id,
                        label: u.name,
                      }))}
                      startIcon={
                        <PersonIcon
                          sx={{
                            color: alpha(NEON, 0.45),
                            fontSize: 18,
                          }}
                        />
                      }
                      placeholder=""
                      sx={{
                        width: '100%',
                        ...(fieldHasError('assigned_to_id')
                          ? {
                              '& .MuiFormHelperText-root': {
                                color: '#F87171',
                              },
                            }
                          : {}),
                      }}
                    />

                    {fieldHelper('assigned_to_id') && (
                      <Typography
                        sx={{
                          fontSize: '0.68rem',
                          color: '#F87171',
                          mt: 0.5,
                          ml: 1.75,
                        }}
                      >
                        {fieldHelper('assigned_to_id')}
                      </Typography>
                    )}
                  </Box>

                  {/* Due Date */}
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <DatePicker
                      label="Due Date"
                      value={formData.due_date}
                      disabled={isReadOnly}
                      minDate={startOfToday()}
                      onChange={handleDueDateChange}
                      open={dueDatePickerProps.open}
                      onOpen={dueDatePickerProps.onOpen}
                      onClose={dueDatePickerProps.onClose}
                      slotProps={dueDatePickerProps.slotProps}
                    />
                  </Box>
                </Box>
              </SectionCard>

              <SectionCard title="Status & Additional Info" icon={<ScheduleIcon />} delay={0.16} accentNeon={NEON3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                  <StyledSelect
                    required label="Status" name="status"
                    value={formData.status} onChange={handleChange}
                    startIcon={<FlagIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  >
                    {STATUS_OPTIONS.map(({ value, label, icon, neon }) => (
                      <MenuItem key={value} value={value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ color: neon, display: 'flex', alignItems: 'center' }}>
                            {React.cloneElement(icon, { sx: { fontSize: 16 } })}
                          </Box>
                          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem' }}>{label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </StyledSelect>

                  <StyledField
                    fullWidth multiline rows={3} label="Additional Notes" name="notes" value={formData.notes}
                    onChange={handleChange}
                    InputProps={{ startAdornment: <InputAdornment position="start"><NotesIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18, alignSelf: 'flex-start', mt: 1.5 }} /></InputAdornment> }}
                  />
                </Box>
              </SectionCard>

              <SubmitActionsCard
                statusText={isUpdatingExisting ? 'Save changes to update this penalty.' : 'Complete the form to create a new penalty.'}
                missingRequiredFields={isUpdatingExisting ? null : [!formData.title?.trim() && 'Title', !formData.description?.trim() && 'Description', !formData.assigned_to_id && 'Assignee', !formData.due_date && 'Due Date', isPage && !isReadOnly && !formData.event_id && 'Event'].filter(Boolean)}
                readyStatusText="All required fields are complete. Ready to create penalty."
                cancelLabel="Cancel"
                onCancel={() => (isPage ? navigate('/penalties') : onClose())}
                cancelDisabled={loading}
                submitLabel={isUpdatingExisting ? 'Update Penalty' : 'Create Penalty'}
                submitDisabled={loading || !isFormValid}
                loading={loading}
                submitStartIcon={<SaveIcon />}
                showSubmit
                submitType="submit"
                {...submitDraftProps}
              />
            </Box>
          )}
        </form>

        {!isReadOnly ? (
          <ConfirmDeleteDialog
            open={confirmUpdateDialogOpen}
            onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
            onConfirm={confirmUpdate}
            title="Confirm Update"
            itemType="penalty"
            variant="edit"
            itemName={formData.title}
            description="This action will update the penalty details and cannot be undone."
            confirmLabel="Update Penalty"
            loading={loading}
            confirmDisabled={!isFormValid}
          />
        ) : null}

        {isReadOnly ? (
          <ConfirmDeleteDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={handleDelete}
            title="Confirm Delete"
            itemType="penalty"
            itemName={penalty?.title}
            description="This action cannot be undone and will permanently remove this penalty."
            confirmLabel="Delete Penalty"
          />
        ) : null}

      </Container>
    </LocalizationProvider>
  );

  return isPage ? formContent : (
    <Box sx={{
      position: 'fixed', inset: 0, background: alpha('#000', 0.6), backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, p: 2,
    }}>
      <Box sx={{ maxHeight: '90vh', overflowY: 'auto', width: '100%', maxWidth: '900px' }}>
        {formContent}
      </Box>
    </Box>
  );
};

export default PenaltyForm;
